import { darken, lighten } from '@mui/material';
import camelize from 'camelize';
import uniqWith from 'lodash/uniqWith';
import React from 'react';
import { EColors, ETeams, getColor } from 'theme';
import { isColorBright } from 'utils/colors';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { getLoggedInUser, updateUser as updateUserAPI } from '../../api';
import { authService } from '../../services/authService';
import { getCookie } from '../../utils/tokens';
import { themeActions } from '../theme';

export const TEAM_ADMIN = 'team-admin';

const initialState: Bizly.User = {
    firstName: '',
    lastName: '',
    phone: '',
    phoneVerifiedAt: null,
    role: '',
    email: '',
    imageUrl: '',
    loaded: false,
    token: '',
    title: '',
    intercomIdentityVerificationHash: '',
    isVenueUser: false,
    platformRole: 'planner',
};

const UNUSED_DUMMY_ID = -1;

const overridingColors: EColors[] = [
    'brand',
    'sidenavForeground',
    'subNavBackground',
    'teamPicker',
    'teamPickerText',
    'homeScreenBanner',
    'bannerLinkText',
    'sidenavColor',
    'sidenavActiveColor',
    'sidenavActiveBackground',
];

const defaultTeamSettings: Partial<Bizly.Team> = {
    imageUrl: 'https://res.cloudinary.com/hdd626jg7/image/upload/v1605123872/bizly-logos/bizly-logo-b.png',
    imageUrlWhite:
        'https://res.cloudinary.com/hdd626jg7/image/upload/e_colorize,co_white/v1605123872/bizly-logos/bizly-logo-b.png',
    color: getColor(EColors.softAccentedBackground),
    bulletinMessage: undefined,
    meetingInternalReferenceFieldLabel: 'Internal Reference',
    meetingCostCenterFieldLabel: 'Cost Center',
    authMeetingCreateRedirect: undefined,
    cancellationInformationMessage: undefined,
    specialCancellationEmail: [],
    showTestingVenues: false,
};

type UserStore = {
    // State
    user: Bizly.User;

    // Internal cache key — prevents duplicate GET /me calls on SPA navigation
    lastFetchedToken: string | null;

    // Computed values
    toggles: {
        gate: Record<string, unknown>;
        compliance: Record<string, unknown>;
    };
    isTeamAdmin: boolean;

    // Actions
    setUserState: (user: Bizly.User) => void;
    clearUser: () => void;
    refreshUser: () => Promise<Bizly.User>;
    updateUser: (user: Partial<Bizly.User>) => Promise<void>;
    setTeam: (team: Bizly.Team) => void;
    fetchMe: () => Promise<void>;
    addConnector: (connector: BizlyAPI.Connector) => void;
    delConnector: (connector: BizlyAPI.Connector) => void;

    // Internal helpers
    overrideColors: (color: string) => void;
    setUserTheme: (user: Partial<Bizly.User>) => void;
};

const useUserStore = create<UserStore>()(
    subscribeWithSelector((set, get) => ({
        // Initial state
        user: initialState,

        // Token cache — tracks last successfully fetched token to avoid duplicate GET /me calls
        lastFetchedToken: null,

        // Computed values (will be updated via subscription)
        toggles: {
            gate: {},
            compliance: {},
        },
        isTeamAdmin: false,

        // Helper functions
        overrideColors: (color: string) => {
            if (color.toLocaleLowerCase() !== defaultTeamSettings.color?.toLowerCase()) {
                const isThemeBright = isColorBright(color);
                const foreground = isThemeBright ? darken(color, 1) : lighten(color, 1);
                const background = isThemeBright ? lighten(color, 1) : darken(color, 1);
                const subNavBg = isThemeBright ? darken(color, 0.2) : lighten(color, 0.2);

                themeActions.setColorOverrides({
                    brand: color,
                    homeScreenBanner: color,
                    bannerLinkText: foreground,
                    teamPicker: foreground,
                    sidenavForeground: foreground,
                    subNavBackground: subNavBg,
                    teamPickerText: background,
                    sidenavColor: isThemeBright ? foreground : lighten(foreground, 0.4),
                    sidenavActiveColor: background,
                    sidenavActiveBackground: isThemeBright ? darken(foreground, 0.5) : lighten(foreground, 1),
                });
            } else {
                themeActions.unsetColorOverrides(overridingColors);
            }
        },

        setUserTheme: (user: Partial<Bizly.User>) => {
            if (!user?.id) return;

            const theme = user?.team?.theme?.toLowerCase() as ETeams;
            themeActions.setTheme(theme || ETeams.bizly);
            if (theme === ETeams.apple) {
                themeActions.unsetColorOverrides(overridingColors);
            } else {
                if (user?.team?.features.enableBranding && user?.team?.color) {
                    get().overrideColors(user.team.color);
                }
            }
        },

        refreshUser: async () => {
            const token = getCookie();
            if (!token) {
                const emptyUser = {
                    ...initialState,
                    email: '',
                    loaded: true,
                    token: '',
                };
                set({ user: emptyUser, lastFetchedToken: null });
                return emptyUser;
            }

            // SHORT-CIRCUIT: if the user is already loaded with this token, skip the network
            // call. This prevents duplicate GET /me on SPA navigation re-mounts, which was
            // the primary cause of spinner delays on every page transition (especially on
            // Edge + corporate proxies where each API call is notably slower).
            const current = get().user;
            if (token && current.loaded && current.token === token && get().lastFetchedToken === token) {
                return current;
            }

            try {
                const loggedInUser = await getLoggedInUser();
                const refreshedUser = {
                    ...loggedInUser,
                    loaded: true,
                    token,
                };
                set({ user: refreshedUser, lastFetchedToken: token });
                get().setUserTheme(refreshedUser);
                return refreshedUser;
            } catch {
                const errorUser = {
                    ...initialState,
                    email: '',
                    loaded: true,
                    token: '',
                };
                set({ user: errorUser, lastFetchedToken: null });
                return errorUser;
            }
        },

        fetchMe: async () => {
            await get().refreshUser();
            // PostHog identification will be handled via subscription
        },

        clearUser: () => {
            set({ user: { ...initialState, loaded: true }, lastFetchedToken: null });
            themeActions.setTheme(ETeams.bizly);
        },

        updateUser: async (userUpdate: Partial<Bizly.User>) => {
            const newUser = await updateUserAPI(userUpdate);
            const updatedUser = { ...get().user, ...newUser };
            set({ user: updatedUser });
            get().setUserTheme(userUpdate);
        },

        setTeam: (team: Bizly.Team) => {
            const theme = (team.theme || ETeams.bizly) as ETeams;
            const updatedUser = { ...get().user, team };
            set({ user: updatedUser });
            themeActions.setTheme(theme);
            if (team.theme === ETeams.apple) {
                themeActions.unsetColorOverrides(overridingColors);
            } else {
                if (team?.features.enableBranding && team?.color) {
                    get().overrideColors(team.color);
                }
            }
        },

        setUserState: (user: Bizly.User) => {
            set({ user });
            get().setUserTheme(user);
            // PostHog identification will be handled via subscription
        },

        addConnector: (connector: BizlyAPI.Connector) => {
            const currentUser = get().user;
            if (currentUser.loaded && currentUser.token) {
                const updatedUser = {
                    ...currentUser,
                    connectors: uniqWith(
                        [...(currentUser.connectors ?? []), { id: UNUSED_DUMMY_ID, type: connector }],
                        (a, b) => a.type === b.type
                    ),
                };
                set({ user: updatedUser });
            }
        },

        delConnector: (connector: BizlyAPI.Connector) => {
            const currentUser = get().user;
            if (currentUser.loaded && currentUser.token) {
                const updatedUser = {
                    ...currentUser,
                    connectors: currentUser.connectors?.filter(curConnector => curConnector.type !== connector),
                };
                set({ user: updatedUser });
            }
        },
    }))
);

// Subscribe to user changes to update computed values
useUserStore.subscribe(
    state => state.user,
    user => {
        const { featureFlags: gate = {} } = user;
        const toggles = {
            gate,
            compliance: {
                ...(user.team?.compliance || {}),
            },
        };

        const isTeamAdmin = user?.team?.role === TEAM_ADMIN;

        useUserStore.setState({
            toggles,
            isTeamAdmin,
        });
    }
);

// Export actions for external use
export const userActions = {
    clearUser: () => useUserStore.getState().clearUser(),
    refreshUser: () => useUserStore.getState().refreshUser(),
    updateUser: (user: Partial<Bizly.User>) => useUserStore.getState().updateUser(user),
    setTeam: (team: Bizly.Team) => useUserStore.getState().setTeam(team),
    fetchMe: () => useUserStore.getState().fetchMe(),
    addConnector: (connector: BizlyAPI.Connector) => useUserStore.getState().addConnector(connector),
    delConnector: (connector: BizlyAPI.Connector) => useUserStore.getState().delConnector(connector),
    setUserState: (user: Bizly.User) => useUserStore.getState().setUserState(user),
};

// Selectors for optimized component subscriptions
export const useUser = () => useUserStore(state => state.user);
export const useUserToggles = () => useUserStore(state => state.toggles);
export const useIsTeamAdmin = () => useUserStore(state => state.isTeamAdmin);

/**
 * Get the user's team tier as a string
 * @param user - The user object
 * @returns The team tier: 'free', 'enterprise', 'corporate', or 'personal'
 */
export const getUserTier = (user: Bizly.User): BizlyAPI.TeamTier => {
    // If user has no team, they are on personal tier
    if (!user.team) {
        return 'personal';
    }

    // Return the team's tier, defaulting to 'free' if not specified
    return user.team.tier || 'free';
};

// For backward compatibility - returns the same shape as the original useUser hook
export const useUserContext = () => {
    const user = useUser();
    const toggles = useUserToggles();
    const isTeamAdmin = useIsTeamAdmin();

    // Get camelized user (same logic as original provider)
    const userStateCamelized = React.useMemo(
        () => ({
            ...user,
            ...(user.team
                ? {
                      team: {
                          ...user.team,
                          ...(user.team?.authMeetingCreateFields
                              ? { authMeetingCreateFields: user.team?.authMeetingCreateFields.map(camelize) }
                              : {}),
                          ...(user.team?.uniqueMeetingCreateFields
                              ? { uniqueMeetingCreateFields: user.team?.uniqueMeetingCreateFields.map(camelize) }
                              : {}),
                          features: {
                              ...user.team.features,
                              ...(user.team.features.hiddenInternalFields
                                  ? { hiddenInternalFields: user.team.features.hiddenInternalFields.map(camelize) }
                                  : {}),
                              ...(user.team.features.requiredInternalFields
                                  ? {
                                        requiredInternalFields: user.team.features.requiredInternalFields.map(camelize),
                                    }
                                  : {}),
                          },
                      },
                  }
                : {}),
        }),
        [user]
    );

    // User tier function
    const userTier = React.useCallback(() => getUserTier(user), [user]);

    return {
        user: userStateCamelized,
        toggles,
        isTeamAdmin,
        userTier,
        clearUser: userActions.clearUser,
        refreshUser: userActions.refreshUser,
        updateUser: userActions.updateUser,
        setUserState: userActions.setUserState,
        setTeam: userActions.setTeam,
        fetchMe: userActions.fetchMe,
        addConnector: userActions.addConnector,
        delConnector: userActions.delConnector,
    };
};

// Register the clearUser handler with the auth service to avoid circular dependency
authService.registerLogoutHandler(() => {
    useUserStore.getState().clearUser();
});

export default useUserStore;
